import React, { useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import StickyFooter from "./components/StickyFooter";
import NavProvider from "./context/NavContext";
import { ColorModeProvider } from "./context/ColorModeContext";
import Appbar from "./components/Appbar";
import ElevationScroll from "./components/ElevationScroll";
import ChatInterface from "./components/ChatInterface";
import AnimatedBackground from "./components/AnimatedBackground";
import Box from "@mui/material/Box";
import "./translation/i18n";
import { pages } from "./pages";

interface Props {
  window?: () => Window;
}

const App: React.FC<Props> = (props) => {
  const [showPortfolio, setShowPortfolio] = useState(false);
  const [backgroundVariant] = useState<'particles' | 'gradient' | 'waves'>('particles');

  const togglePortfolio = () => {
    setShowPortfolio(!showPortfolio);
  };

  return (
    <ColorModeProvider>
      <CssBaseline />
      {/* Animated background that appears on all pages */}
      <AnimatedBackground variant={backgroundVariant} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh', // Full viewport height
          position: 'relative', // For proper stacking with background
        }}
      >
        <NavProvider>
          {/* AppBar with navigation links conditionally shown */}
          <ElevationScroll {...props}>
            <Appbar 
              hideNavLinks={!showPortfolio} 
              hideLanguageMenu={!showPortfolio} 
              onTogglePortfolio={togglePortfolio}
            />
          </ElevationScroll>

          {/* Main content area that will flex-grow to fill available space */}
          <Box 
            component="main"
            sx={{ 
              flexGrow: 1, 
              display: "flex",
              flexDirection: "column",
              pt: 8, // Add padding to account for AppBar
              position: "relative", // Ensures content is above the background
              zIndex: 1,
            }}
          >
            {/* Conditionally render portfolio or chat */}
            {showPortfolio ? (
              // Portfolio content
              <>{pages.map((page) => page.component)}</>
            ) : (
              // Full page chat interface
              <Box sx={{ 
                display: "flex", 
                flexDirection: "column", 
                flexGrow: 1,
              }}>
                <ChatInterface fullPage={true} />
              </Box>
            )}
          </Box>
          
          {/* Footer will always stay at the bottom */}
          <StickyFooter />
        </NavProvider>
      </Box>
    </ColorModeProvider>
  );
};

export default App;
